import { useLayoutEffect, useRef, useState } from 'react'

import { Popover, Tooltip } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { ArrowUpRight } from 'lucide-react'

import { navigate, routes } from '@redwoodjs/router'
import { useQuery } from '@redwoodjs/web'

import { GET_PSP_FINDINGS } from 'src/graphql/documents.graphql'
import IconChevronDown from 'src/icons/IconChevronDown'
import {
  formatExtractedDetailValue,
  MvrExtractedFinding,
  PSP_COUNTED_FINDING_TYPES,
  pspFindingsFromExtractedRows,
} from 'src/lib/documentIntelligence'

import { getUtcMonth } from '../../utils'

// ENG-4335: PSP mirror of MvrFindingsPill. Sits on the Accidents & Violations
// card header alongside MvrFindingsPill and surfaces what the AI read off the
// applicant's PSP so a recruiter can reconcile it against the driver-disclosed
// entries below without opening the document. Admin-only: the parent
// AccidentsCard only renders this pill for admin users during the PSP rollout.
//
// Red only when the PSP shows more crashes + violations than the driver
// disclosed (undisclosed events); yellow when the counts match or the driver
// disclosed more than the PSP surfaces. Hidden when there are no findings on
// the applicant's PSP.

// PSP findings this pill surfaces on the card. INSPECTION is excluded here —
// it's not a self-disclosed event, so it's noise for the reconcile-against-
// application use case. Same MvrFindingsPill scoping decision, mirrored.
const SECTION_FINDING_TYPES = ['CRASH', 'VIOLATION']

const pluralize = (count: number, noun: string) =>
  `${count} ${noun}${count === 1 ? '' : 's'}`

// "1 crash · 3 violations" — zero categories omitted.
const buildCountLabel = (findings: MvrExtractedFinding[]) => {
  const crashes = findings.filter((f) => f.findingType === 'CRASH').length
  const violations = findings.filter((f) => f.findingType === 'VIOLATION').length
  return [
    crashes > 0 && pluralize(crashes, 'crash'),
    violations > 0 && pluralize(violations, 'violation'),
  ].filter(Boolean) as string[]
}

// PSP crash/violation dates come off the report as year-only for older events
// (FMCSA lookback) — render MM/YYYY when we have a date so the card cross-
// references cleanly against driver-disclosed entries (which the accidents
// card renders the same way).
const formatFindingDate = (date: string | null) => getUtcMonth(date, '—')

// Tracks whether a truncating element is actually clipping its text, so the
// hover tooltip only appears when there's hidden content to reveal.
const useIsOverflowing = () => {
  const ref = useRef<HTMLSpanElement>(null)
  const [isOverflowing, setIsOverflowing] = useState(false)

  useLayoutEffect(() => {
    const node = ref.current
    if (!node) return
    const check = () =>
      setIsOverflowing(node.scrollWidth > node.clientWidth + 1)
    check()
    const observer = new ResizeObserver(check)
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  return { ref, isOverflowing }
}

const TruncatedText = ({
  text,
  className,
}: {
  text: string
  className?: string
}) => {
  const { ref, isOverflowing } = useIsOverflowing()
  return (
    <Tooltip
      label={text}
      position="top-start"
      offset={4}
      withArrow
      withinPortal
      multiline
      maw={280}
      disabled={!isOverflowing}
    >
      <span ref={ref} className={`block min-w-0 truncate ${className ?? ''}`}>
        {text}
      </span>
    </Tooltip>
  )
}

// Singular per-finding category label ("PSP crash", "PSP violation").
const findingTypeLabel = (findingType: string): string => {
  switch (findingType) {
    case 'CRASH':
      return 'PSP crash'
    case 'VIOLATION':
      return 'PSP violation'
    case 'INSPECTION':
      return 'PSP inspection'
    default:
      return findingType
  }
}

const FindingRow = ({ finding }: { finding: MvrExtractedFinding }) => {
  // Mirror the MVR pill layout so the two cards read the same way (ENG-4406):
  // bold state as the heading; finding type as the subtitle; description as
  // lowercase secondary text; every other extracted field as label/value.
  // Location on PSP is the parent inspection's 2-letter state (populated by
  // aiExtractedRows for VIOLATION via join, and directly for CRASH /
  // INSPECTION from PSP_*_RPT_STATE). Falls back to em-dash when the state
  // wasn't extracted, matching MvrFindingsPill's null-location placeholder.
  const location = finding.location

  return (
    <div
      data-testid="psp-finding-row"
      className="border-b border-doubleNickel-gray-100 px-4 py-3 last:border-b-0"
    >
      <div className="flex items-start gap-2">
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex items-center gap-2">
            {location ? (
              <TruncatedText
                text={location}
                className="text-xs font-semibold text-doubleNickel-gray-900"
              />
            ) : (
              <span className="text-xs font-semibold text-doubleNickel-gray-900">
                —
              </span>
            )}
          </div>
          {/* Type subtitle: mirrors MVR's ("1 accident" style, text-xs gray-500). */}
          <span className="text-xs text-doubleNickel-gray-500">
            {findingTypeLabel(finding.findingType)}
          </span>
          {finding.description && (
            // PSP descriptions arrive mostly upper-case (violation text) or as
            // free-form crash location strings; render lowercase with a
            // first-letter cap via CSS so the pill reads like prose, matching
            // the MVR pill treatment.
            <TruncatedText
              text={finding.description}
              className="text-xs lowercase text-doubleNickel-gray-500 first-letter:uppercase"
            />
          )}
          {/* Every other extracted field (Report #, Carrier, OOS, …) renders
              as label/value lines, matching the doc-preview hover. */}
          {finding.details.map((detail) => (
            <span
              key={detail.label}
              className="text-xs text-doubleNickel-gray-500"
            >
              {detail.label}: {formatExtractedDetailValue(detail.value)}
            </span>
          ))}
        </div>
        <span
          className="whitespace-nowrap text-xs text-doubleNickel-gray-700"
          style={{ fontFamily: 'monospace' }}
        >
          {formatFindingDate(finding.date)}
        </span>
      </div>
    </div>
  )
}

interface PspFindingsPillProps {
  applicantId: string
  disclosedAccidentCount: number
}

const PspFindingsPill = ({
  applicantId,
  disclosedAccidentCount,
}: PspFindingsPillProps) => {
  const [opened, { toggle, close }] = useDisclosure(false)

  const { data } = useQuery(GET_PSP_FINDINGS, {
    variables: { applicantId },
    skip: !applicantId,
    fetchPolicy: 'cache-first',
  })

  // Newest PSP with completed AI analysis and at least one surfaced finding.
  // Same selection semantics as MvrFindingsPill so the two card-level pills
  // and the header-level AiFlagsPill all describe the applicant's current
  // report of record.
  let findings: MvrExtractedFinding[] | null = null
  let documentId: string | null = null
  for (const doc of data?.documents?.items ?? []) {
    if (doc.documentIntelligence?.status !== 'COMPLETED') continue
    const allSurfaced = pspFindingsFromExtractedRows(
      doc.documentIntelligence.aiExtractedRows ?? []
    ).filter((finding) =>
      PSP_COUNTED_FINDING_TYPES.includes(finding.findingType)
    )
    if (allSurfaced.length > 0) {
      findings = allSurfaced.filter((finding) =>
        SECTION_FINDING_TYPES.includes(finding.findingType)
      )
      documentId = doc.documentId
      break
    }
  }

  const openDocument = () => {
    if (!documentId) return
    navigate(
      routes.applicantDetails({
        id: applicantId,
        tab: 'documents',
        subtab: 'documents',
        documentId,
      })
    )
  }

  // Empty when the applicant has PSP inspections only (no crashes/violations)
  // — those are already surfaced at the header level by AiFlagsPill, so
  // rendering a card pill with no rows would be noise.
  if (!findings || findings.length === 0) return null

  // Same rule as MvrFindingsPill / DI page: red only when the PSP reveals MORE
  // incidents than the driver disclosed on the application. Under-disclosure
  // (PSP shows fewer than disclosed) is a normal FMCSA lookback artifact — an
  // older accident aged out of the report — not a recruiter risk. See
  // fix(ENG-4333) for the same semantic on the DI page.
  const pspIncidentCount = findings.length
  const hasUndisclosedIncidents = pspIncidentCount > disclosedAccidentCount
  const countLabel = buildCountLabel(findings)

  const pillColors = hasUndisclosedIncidents
    ? 'border-doubleNickel-error-200 bg-doubleNickel-error-25 text-doubleNickel-error-700'
    : 'border-doubleNickel-warning-100 bg-doubleNickel-warning-25 text-doubleNickel-warning-700'
  const dotColor = hasUndisclosedIncidents
    ? 'bg-doubleNickel-error-500'
    : 'bg-doubleNickel-warning-500'

  return (
    <Popover
      position="bottom-end"
      offset={6}
      shadow="md"
      width={380}
      opened={opened}
      onDismiss={close}
    >
      <Popover.Target>
        <button
          data-testid="psp-findings-pill"
          className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 text-[11px] font-semibold ${pillColors}`}
          onClick={(e) => {
            e.stopPropagation()
            toggle()
          }}
        >
          <span className={`h-1.5 w-1.5 rounded-full ${dotColor}`} />
          {countLabel.map((part, i) => (
            <span key={part} className="inline-flex items-center gap-1.5">
              {i > 0 && <span className="font-normal opacity-50">·</span>}
              {part}
            </span>
          ))}
          <IconChevronDown
            className={`h-3.5 w-3.5 fill-none stroke-current transition-transform ${
              opened ? 'rotate-180' : ''
            }`}
          />
        </button>
      </Popover.Target>
      <Popover.Dropdown
        className="p-0"
        onClick={(e) => e.stopPropagation()}
        data-testid="psp-findings-dropdown"
      >
        <div className="border-b border-doubleNickel-gray-100 px-4 pb-2.5 pt-3.5">
          <div className="flex items-center justify-between gap-2">
            <div className="text-sm font-semibold text-doubleNickel-gray-900">
              PSP
            </div>
            {documentId && (
              <button
                type="button"
                data-testid="psp-findings-view-document"
                className="inline-flex shrink-0 items-center gap-0.5 text-xs font-medium text-doubleNickel-brand-500 hover:underline"
                onClick={(e) => {
                  e.stopPropagation()
                  openDocument()
                }}
              >
                View PSP
                <ArrowUpRight size={13} />
              </button>
            )}
          </div>
          <div className="mt-0.5 text-xs text-doubleNickel-gray-500">
            {countLabel.join(' · ')}
          </div>
        </div>
        <div className="max-h-80 overflow-y-auto">
          {findings.map((finding) => (
            <FindingRow key={finding.key} finding={finding} />
          ))}
        </div>
      </Popover.Dropdown>
    </Popover>
  )
}

export default PspFindingsPill
